import type { PipelineTrace } from '../../api/types';

interface Props {
  traces: PipelineTrace[];
}

const STAGE_LABELS: Record<string, string> = {
  vector_search: 'Vector Search',
  detailed_scoring: 'Scoring',
  gates: 'Gates',
  llm_judge: 'LLM Judge',
  fusion: 'Fusion',
};

const STAGE_COLORS: Record<string, string> = {
  vector_search: 'bg-blue-500',
  detailed_scoring: 'bg-indigo-500',
  gates: 'bg-amber-500',
  llm_judge: 'bg-purple-500',
  fusion: 'bg-green-500',
};

export default function DecisionFlow({ traces }: Props) {
  if (traces.length === 0) return null;

  const totalTime = traces.reduce((sum, t) => sum + t.latencyMs, 0);

  return (
    <div className="card">
      <h3 className="font-semibold text-gray-900 mb-4">Pipeline Flow</h3>

      <div className="flex items-center gap-2 overflow-x-auto pb-4">
        {traces.map((trace, i) => (
          <div key={i} className="flex items-center">
            {/* Stage Box */}
            <div className="min-w-[120px] text-center">
              <div
                className={`${STAGE_COLORS[trace.stage] || 'bg-gray-500'}
                  text-white rounded-lg px-4 py-3`}
              >
                <div className="text-xs opacity-80">
                  {STAGE_LABELS[trace.stage] || trace.stage}
                </div>
                <div className="text-2xl font-bold">{trace.outputCount}</div>
                <div className="text-xs opacity-80">
                  {trace.latencyMs}ms
                </div>
              </div>

              {/* Drop info */}
              {trace.inputCount !== trace.outputCount && (
                <div className="text-xs text-gray-500 mt-1">
                  {trace.inputCount} → {trace.outputCount}
                  <span className="text-red-500 ml-1">
                    (-{trace.inputCount - trace.outputCount})
                  </span>
                </div>
              )}
            </div>

            {/* Arrow */}
            {i < traces.length - 1 && (
              <div className="px-2 text-gray-300 text-2xl">→</div>
            )}
          </div>
        ))}
      </div>

      {/* Timing Bar */}
      <div className="mt-4 pt-4 border-t">
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
          <span>Timing breakdown</span>
          <span className="ml-auto">{totalTime}ms total</span>
        </div>
        <div className="flex h-4 rounded overflow-hidden">
          {traces.map((trace, i) => {
            const width = (trace.latencyMs / totalTime) * 100;
            return (
              <div
                key={i}
                className={`${STAGE_COLORS[trace.stage] || 'bg-gray-500'} opacity-70`}
                style={{ width: `${width}%` }}
                title={`${STAGE_LABELS[trace.stage]}: ${trace.latencyMs}ms`}
              />
            );
          })}
        </div>
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          {traces.map((trace, i) => (
            <span key={i}>
              {STAGE_LABELS[trace.stage]?.split(' ')[0]}: {trace.latencyMs}ms
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
